import { useMemo, useState } from 'react';

// Components
import { ConfigContext, SMPContainer } from '@velinsi/smp-widgets-ui';
import EventInfo from '../../components/EventSection/EventInfo/EventInfo';

// Providers
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';

import { useIsSmartphoneDevice } from '../../../../../packages/hooks/useIsSmartphoneDevice';

// Types
import { BaseWidgetProps, INormalizedTennisParticipantDetails, ITennisSingleEventConfig } from '../../../../../packages/types';

// Misc
import { GenericSize, MAX_WIDGET_WIDTH } from '../../../../../packages/constants/generic';
import { normalizeTennisMatchData } from '../../normalizers/normalizeTennisMatchData';

// Hooks
import { defaultHandleEventClick, defaultHandleParticipantClick, extractOnlyDateSDKOptions } from '@sportal365/smp-widget-utils';
import useSingleEvent from '../../hooks/useSingleEvent';
import useResizeObserver from '../../../../../packages/hooks/useResizeObserver';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import { LinkWrapper } from '../../../../../packages/HOC/LinkWrapper';
import useOverlapOdds from '../../../../../packages/hooks/useOverlapOdds';

export type SingleEventWidgetProps = BaseWidgetProps<ITennisSingleEventConfig>;

export const SingleEventWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: SingleEventWidgetProps) => {
  const { ref } = useRootNodeRef();
  const { areBettingOddsOverlapVisible } = useOverlapOdds({ dataBettingLogoOverlap: config?.dataBettingLogoOverlap });

  const {
    showWidget,
    handleClick,
    widgetData: { match, isLoading },
  } = useSingleEvent({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataMatchId: config?.dataMatchId,
    dataOddsDisplay: config?.dataOddsDisplay,
    dataOddsMarket: config?.dataOddsMarket,
    dataOddsMarketValueType: config?.dataOddsMarketValueType,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataRefreshTime: config?.dataRefreshTime,
    dataWidgetCuid: cuid,
  });

  const normalizedMatch = useMemo(
    () =>
      normalizeTennisMatchData({
        match,
        dataEntityLinks: config?.dataEntityLinks,
        dataShortStatusType: config?.dataShortStatusType,
        sdkDateOptions: extractOnlyDateSDKOptions(sdkOptions),
      }),
    [match, config, sdkOptions],
  );

  const { isMobile } = useIsSmartphoneDevice(ref);

  const [width, setWidth] = useState(MAX_WIDGET_WIDTH);
  const measuredRef = useResizeObserver((fieldElement) => {
    setWidth(fieldElement.offsetWidth);
  });

  if (!showWidget) return null;

  const handleParticipantClick = ({ id, name, entityType }: INormalizedTennisParticipantDetails) =>
    defaultHandleParticipantClick(handleClick, { id, name, entityType });

  const handleEventClick = (event: string | number) => defaultHandleEventClick(handleClick, Number(event));

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <div ref={ref}>
        <ThemeProvider isLoading={isLoading} shadow={shadow} themes={themes} dataTheme={config?.dataTheme}>
          {normalizedMatch && normalizedMatch.id && (
            <LinkWrapper
              url={normalizedMatch.matchLink}
              target={normalizedMatch.urlTarget}
              shouldWrap={config?.dataWrapperLink}
              onClick={() => normalizedMatch.id && handleEventClick(normalizedMatch.id)}
            >
              <SMPContainer ref={measuredRef} variant='paddinglessContainer'>
                <EventInfo
                  isMobile={isMobile}
                  showOdds={config?.dataOddsDisplay}
                  participants={normalizedMatch?.participants}
                  match={normalizedMatch}
                  handleParticipantClick={handleParticipantClick}
                  handleEventClick={handleEventClick}
                  handleClick={handleClick}
                  matchContainerType='singleEvent'
                  showCurrentServer
                  size={GenericSize.SMALL}
                  width={width}
                  wrapperLink={false}
                  areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
                />
              </SMPContainer>
            </LinkWrapper>
          )}
        </ThemeProvider>
      </div>
    </ConfigContext.Provider>
  );
};
